import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { BaseUrl } from '../../../framework/constants/url-constants';
import { BehaviorSubject, catchError, from, Observable, Subject, switchMap, throwError } from 'rxjs';
import { FileUploadService } from '../../shared/components/file-upload/file-upload.service';

@Injectable({
  providedIn: 'root'
})
export class PatientService {

  private applyNewTargetButtonSubject = new BehaviorSubject<boolean>(false);
  enableButton$ = this.applyNewTargetButtonSubject.asObservable();
  private analysisRequestedSource = new Subject<boolean>();
  analysisRequested = this.analysisRequestedSource.asObservable();

  private patientIdSubject = new BehaviorSubject<number | null>(null);
  patientId$ = this.patientIdSubject.asObservable();

  setPatientId(id: number | null) {
    this.patientIdSubject.next(id);
  }

  constructor(private readonly http: HttpClient,
    private readonly fileUploadService:FileUploadService    
    ) { }


  createPatient(payload) {
    return this.http.post<any>(`${BaseUrl.PATIENT}/patients`, payload);
  }

  findAllPaginated(sort: string, page: number, size: number, keyword: string): Observable<any> {
    const href = `${BaseUrl.PATIENT}/patients`;
    return this.http.get<any>(href, {
      params: new HttpParams()
        .set('sort', sort.toString())
        .set('page', page.toString())
        .set('size', size.toString())
        .set('keyword', keyword)
    });
  }

  getPatientDetails(id) {
    const href = `${BaseUrl.PATIENT}/patients/${id}`;
    return this.http.get<any>(href);
  }


  updatePatient(payload) {
    const href =`${BaseUrl.PATIENT}/patients/${payload.id}`;
    return this.http.put(href, payload );
  }

  resolveLoggedInUserRole() {
    return JSON.parse(atob(localStorage.getItem('token').split('.')[1])).roleId;
  }

  placeGenomeDataRequest(payload){
    const href =`${BaseUrl.PATIENT}/genome-data/request`;
    return this.http.post(href, payload );
  }

  getGenomeData(id) {
    const href = `${BaseUrl.PATIENT}/genome-data/${id}`;
    return this.http.get<any>(href);
  }

  internalTransfer(payload){
    const href =`${BaseUrl.PATIENT}/genome-data/internal-transfer`;
    return this.http.post(href, payload );
  }

  downloadFile(filePath): Observable<any>{
    const href =`${BaseUrl.PATIENT}/genome-data/analysis-report/download`;
		return this.http.post(href, filePath,{responseType: 'blob',observe: 'response'});
  }
  
  findAllPatientReport(page: number, body: any): Observable<any> {
    const href = `${BaseUrl.PATIENT}/report/patient`;
    return this.http.post<any>(href, body, {
      params: new HttpParams()
        .set('page', page.toString())
    });
  }

  viewReport(patientId , templateId){
    const href = `${BaseUrl.PATIENT}/report/view/${patientId}/${templateId}`;
    return this.http.get<any>(href);
  }

  findReportStatusByPatientId(patientId){
    const href = `${BaseUrl.PATIENT}/report/status/${patientId}`;
    return this.http.get<any>(href);
  }

  findHGAlignmentStatus(patientId){
  const href = `${BaseUrl.PATIENT}/patients/hgAlignmentStatus/${patientId}`;
  return this.http.get<any>(href);
  }

  runHG19Alignment(payload){
   const href = `${BaseUrl.PATIENT}/report/run`;
   return this.http.post(href, payload );
  }

  findSereakRunStatePatient(){
    const href = `${BaseUrl.PATIENT}/genome-data/sereak-run/state`;
    return this.http.get<any>(href);
   }


   findSereakRunStateControl(){
    const href = `${BaseUrl.CONTROL}/genomic/sereak/run/state`;
    return this.http.get<any>(href);
   }

  downloadReportFile(filePath, templateName): Observable<any>{
    const href =`${BaseUrl.PATIENT}/report/template-report/download`;
    const requestBody = { filePath, templateName};
    return this.http.post(href, requestBody, { responseType: 'blob', observe: 'response' }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 422 && error.error instanceof Blob) {
          return from(error.error.text()).pipe(
            switchMap((errorText: string) => {
              try {
                const errorData = JSON.parse(errorText);
                
                const applicationMessage = errorData?.applicationMessage || 'An error occurred';
               
                return throwError(() => applicationMessage);
              } catch (e) {
                return throwError(() => new Error('Error parsing error response'));
              }
            })
          );
        }
      })
    );
  }
   uploadFile(patientId: number, file: File) {
    const url =`${BaseUrl.PATIENT}/patients/${patientId}/consent-upload`;
    const formData = new FormData();
    formData.append('file', file);
    return this.fileUploadService.uploadFile(url, formData,'POST');
  }

  getAllConsentFileNames(id: number) {
    const href = `${BaseUrl.PATIENT}/patients/${id}/consent-files`;
    return this.http.get<any>(href);
  }

  deleteFileFromStorage(patientId,payload){
    return this.http.delete<any>(`${BaseUrl.PATIENT}/patients/${patientId}/delete-consent`, {body:{ filename: payload.fileName }});
  }

  setEnableButtonState(flag: boolean): void {
    this.applyNewTargetButtonSubject.next(flag);
  }

  findInProgressReportStatusByPatientId(patientId){
    const href = `${BaseUrl.PATIENT}/report/in-progress-status/${patientId}`;
    return this.http.get<any>(href);
  }

  getPrincipalPatient(){
    const href = `${BaseUrl.PATIENT}/patients/has-principal`;
    return this.http.get<any>(href);
  }

  getPatientbyId(){
   const href = `${BaseUrl.PATIENT}/patients/get-patient`;
    return this.http.get<any>(href);

  }

  checkAtLeastOneReportAvailable(patientId) {
    const href = `${BaseUrl.PATIENT}/report/${patientId}/has-report-run`;
    return this.http.get<any>(href);
  }

  checkPatientsConsent(patientId: number) {
    const href = `${BaseUrl.PATIENT}/patient-consent/${patientId}/is-consent`;
    return this.http.get<any>(href);
  }

  getAllPatientsConsent(identifier: string) {
    const href = `${BaseUrl.PATIENT}/ref-data-choice/refDataIdentifier/${identifier}`;
    return this.http.get<any>(href);
  }

  addPatientConsent(payload){
    const href = `${BaseUrl.PATIENT}/patient-consent`;
    return this.http.post(href, payload );
  }

  getAllPackageTemplates(){
    const href = `${BaseUrl.TEMPLATE}/package-templates`;
    return this.http.get<any>(href);
  }

  savePatientAnalysisRequest(payload){
    const href = `${BaseUrl.PATIENT}/patients/save-analysis-request`;
    return this.http.post(href, payload );
  }

  getPatientAnalysisRequestStatus(patientId){
    const href = `${BaseUrl.PATIENT}/report/${patientId}/has-analysis-requested`;
    return this.http.get<any>(href);
  }

  notifyAnalysisRequested(value: boolean) {
    this.analysisRequestedSource.next(value);
  }
  
  sendAnalysisRequestNotification(data){
 const href = `${BaseUrl.USER}/user/notify-analysis-request`;
    return this.http.post(href,data);

  }
}
